import * as React from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { getMyCredits } from "@/lib/account.functions";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "@/lib/toast";

export const Route = createFileRoute("/_authed/team")({
  component: TeamPage,
});

type Member = {
  user_id: string;
  first_name: string | null;
  email: string | null;
  role: string;
  is_owner: boolean;
};

type MyInvitation = {
  id: string;
  team_id: string;
  team_name: string;
  inviter_email: string;
  created_at: string;
};

type TeamInvitation = {
  id: string;
  invited_email: string;
  created_at: string;
};

const ERROR_MAP: Record<string, string> = {
  invitee_not_found: "No CapitalDart account with that email",
  invitee_in_another_team: "That person is already on another team",
  already_member: "That person is already on your team",
  cannot_invite_self: "You are already on the team",
  insufficient_credits: "You need at least 10 credits to invite a member",
  team_full: "Teams are limited to 25 members for now",
  owner_cannot_leave: "Team owners cannot leave their own team",
  no_team: "You are not on a team",
  not_owner: "Only the team owner can do that",
};

type PgLike = {
  message?: string;
  details?: string | null;
  hint?: string | null;
  code?: string | null;
};

function mapFriendly(message: string): string | null {
  for (const code of Object.keys(ERROR_MAP)) {
    if (message.includes(code)) return ERROR_MAP[code];
  }
  return null;
}

function formatErr(err: unknown): string {
  const e = (err ?? {}) as PgLike;
  const msg = e.message ?? String(err);
  const friendly = mapFriendly(msg);
  const parts: string[] = [];
  if (friendly) parts.push(friendly);
  parts.push(`raw: ${msg}`);
  if (e.details) parts.push(`details: ${e.details}`);
  if (e.hint) parts.push(`hint: ${e.hint}`);
  if (e.code) parts.push(`code: ${e.code}`);
  return parts.join(" · ");
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  } catch {
    return iso;
  }
}

function TeamPage() {
  const { user } = useAuth();
  const qc = useQueryClient();
  const getCreditsFn = useServerFn(getMyCredits);

  const membersQ = useQuery({
    queryKey: ["team", "members"],
    queryFn: async (): Promise<Member[]> => {
      const { data, error } = await supabase.rpc("get_team_members");
      if (error) throw new Error(error.message);
      return (data ?? []) as Member[];
    },
  });

  const creditsQ = useQuery({
    queryKey: ["credits"],
    queryFn: () => getCreditsFn(),
  });

  const myInvitesQ = useQuery({
    queryKey: ["team", "my-invitations"],
    queryFn: async (): Promise<MyInvitation[]> => {
      const { data, error } = await supabase.rpc("get_my_invitations");
      if (error) throw new Error(error.message);
      return (data ?? []) as MyInvitation[];
    },
  });

  const me = membersQ.data?.find((m) => m.user_id === user?.id);
  const isOwner = !!me?.is_owner;

  const teamNameQ = useQuery({
    queryKey: ["team", "name"],
    enabled: !!me,
    queryFn: async (): Promise<string | null> => {
      const { data, error } = await supabase
        .from("teams")
        .select("name")
        .limit(1)
        .maybeSingle();
      if (error) throw new Error(error.message);
      return (data?.name as string | null) ?? null;
    },
  });

  const pendingQ = useQuery({
    queryKey: ["team", "pending-invitations"],
    enabled: isOwner,
    queryFn: async (): Promise<TeamInvitation[]> => {
      const { data, error } = await supabase.rpc("get_team_invitations");
      if (error) throw new Error(error.message);
      return (data ?? []) as TeamInvitation[];
    },
  });

  const refreshAll = () => {
    qc.invalidateQueries({ queryKey: ["team", "members"] });
    qc.invalidateQueries({ queryKey: ["team", "my-invitations"] });
    qc.invalidateQueries({ queryKey: ["team", "pending-invitations"] });
    qc.invalidateQueries({ queryKey: ["team", "name"] });
    qc.invalidateQueries({ queryKey: ["credits"] });
    qc.invalidateQueries({ queryKey: ["my-credits"] });
  };

  const [email, setEmail] = React.useState("");
  const [addError, setAddError] = React.useState<string | null>(null);
  const [removeError, setRemoveError] = React.useState<string | null>(null);
  const [inviteError, setInviteError] = React.useState<string | null>(null);
  const [leaveError, setLeaveError] = React.useState<string | null>(null);

  const addM = useMutation({
    mutationFn: async (invitee: string) => {
      const { error } = await supabase.rpc("create_team_invitation", {
        p_invitee_email: invitee,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invitation sent");
      setEmail("");
      setAddError(null);
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("create_team_invitation failed:", e);
      const text = formatErr(e);
      setAddError(text);
      const friendly =
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Invite failed";
      toast.error(friendly, { context: "team.invite" });
    },
  });

  const removeM = useMutation({
    mutationFn: async (userId: string) => {
      const { error } = await supabase.rpc("remove_team_member", {
        p_member_user_id: userId,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Member removed");
      setRemoveError(null);
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("remove_team_member failed:", e);
      const text = formatErr(e);
      setRemoveError(text);
      const friendly =
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Remove failed";
      toast.error(friendly, { context: "team.remove" });
    },
  });

  const acceptM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("accept_team_invitation", {
        p_invitation_id: id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invitation accepted");
      setInviteError(null);
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("accept_team_invitation failed:", e);
      setInviteError(formatErr(e));
      toast.error(
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Accept failed",
        { context: "team.accept" },
      );
    },
  });

  const declineM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("decline_team_invitation", {
        p_invitation_id: id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invitation declined");
      setInviteError(null);
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("decline_team_invitation failed:", e);
      setInviteError(formatErr(e));
      toast.error(
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Decline failed",
        { context: "team.decline" },
      );
    },
  });

  const cancelM = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.rpc("cancel_team_invitation", {
        p_invitation_id: id,
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Invitation cancelled");
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("cancel_team_invitation failed:", e);
      toast.error(
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Cancel failed",
        { context: "team.cancel" },
      );
    },
  });

  const leaveM = useMutation({
    mutationFn: async () => {
      const { error } = await supabase.rpc("leave_team");
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("You left the team");
      setLeaveError(null);
      refreshAll();
    },
    onError: (e: unknown) => {
      console.error("leave_team failed:", e);
      setLeaveError(formatErr(e));
      toast.error(
        mapFriendly((e as PgLike)?.message ?? String(e)) ?? "Leave failed",
        { context: "team.leave" },
      );
    },
  });

  const myInvites = myInvitesQ.data ?? [];
  const teamName = teamNameQ.data || "Team";

  return (
    <main className="mx-auto w-full max-w-3xl px-4 py-10">
      <h1 className="text-2xl font-semibold">{teamName}</h1>

      {myInvites.length > 0 && (
        <section className="mt-4 rounded-md border bg-card p-4">
          <h2 className="text-sm font-medium">Pending invitations for you</h2>
          {inviteError && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {inviteError}
            </p>
          )}
          <ul className="mt-3 space-y-4">
            {myInvites.map((inv) => (
              <li key={inv.id} className="rounded-md border p-3">
                <div className="text-sm">
                  <span className="font-medium">{inv.inviter_email}</span>{" "}
                  invited you to join{" "}
                  <span className="font-medium">{inv.team_name}</span>
                </div>
                <p className="mt-2 text-xs text-muted-foreground">
                  If you accept, you join this team, your existing deals and
                  credits move into the team's shared pool, and your solo
                  account is replaced by your seat on the team.
                </p>
                <div className="mt-3 flex gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={acceptM.isPending || declineM.isPending}
                    onClick={() => acceptM.mutate(inv.id)}
                  >
                    {acceptM.isPending ? "Accepting…" : "Accept"}
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={acceptM.isPending || declineM.isPending}
                    onClick={() => declineM.mutate(inv.id)}
                  >
                    Decline
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        </section>
      )}

      <p className="mt-4 text-sm text-muted-foreground">
        Members share one credit pool. Each accepted invitation costs 10
        credits from the pool at the moment the invitee accepts. Pending
        invitations are not charged. Removing a member does not refund credits.
      </p>
      <div className="mt-4 rounded-md border bg-card p-4 text-sm">
        <span className="text-muted-foreground">Pool balance: </span>
        <span className="font-medium">
          {creditsQ.data ? `${creditsQ.data.credits_remaining} credits` : "..."}
        </span>
      </div>

      {isOwner && (
        <div className="mt-6 rounded-md border bg-card p-4">
          <h2 className="text-sm font-medium">Invite a member</h2>
          <form
            className="mt-3 flex flex-col gap-2 sm:flex-row"
            onSubmit={(e) => {
              e.preventDefault();
              const v = email.trim();
              if (!v) return;
              setAddError(null);
              addM.mutate(v);
            }}
          >
            <Input
              type="email"
              required
              placeholder="invitee@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="sm:flex-1"
            />
            <Button type="submit" disabled={addM.isPending}>
              {addM.isPending ? "Sending…" : "Send invite"}
            </Button>
          </form>
          {addError && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {addError}
            </p>
          )}
          <p className="mt-2 text-xs text-muted-foreground">
            The 10-credit charge happens when the invitee accepts.
          </p>
        </div>
      )}

      {isOwner && (
        <div className="mt-6 rounded-md border bg-card">
          <div className="border-b px-4 py-3 text-sm font-medium">
            Pending invitations sent
          </div>
          {pendingQ.isLoading ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              Loading...
            </div>
          ) : pendingQ.error ? (
            <div className="px-4 py-6 text-sm text-destructive">
              {(pendingQ.error as Error).message}
            </div>
          ) : (pendingQ.data ?? []).length === 0 ? (
            <div className="px-4 py-6 text-sm text-muted-foreground">
              No pending invitations.
            </div>
          ) : (
            <ul className="divide-y">
              {pendingQ.data?.map((inv) => (
                <li
                  key={inv.id}
                  className="flex items-center gap-3 px-4 py-3"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">
                      {inv.invited_email}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Sent {formatDate(inv.created_at)}
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    disabled={cancelM.isPending}
                    onClick={() => {
                      if (
                        window.confirm(
                          `Cancel the invitation to ${inv.invited_email}?`,
                        )
                      ) {
                        cancelM.mutate(inv.id);
                      }
                    }}
                  >
                    Cancel
                  </Button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <div className="mt-6 rounded-md border bg-card">
        <div className="border-b px-4 py-3 text-sm font-medium">Members</div>
        {removeError && (
          <div className="border-b px-4 py-2 text-xs text-destructive" role="alert">
            {removeError}
          </div>
        )}
        {membersQ.isLoading ? (
          <div className="px-4 py-6 text-sm text-muted-foreground">Loading...</div>
        ) : membersQ.error ? (
          <div className="px-4 py-6 text-sm text-destructive">
            {(membersQ.error as Error).message}
          </div>
        ) : (
          <ul className="divide-y">
            {membersQ.data?.map((m) => {
              const name = m.first_name?.trim() || m.email || m.user_id;
              const canRemove = isOwner && !m.is_owner;
              return (
                <li key={m.user_id} className="flex items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium">{name}</div>
                    {m.email && (
                      <div className="truncate text-xs text-muted-foreground">
                        {m.email}
                      </div>
                    )}
                  </div>
                  <Badge variant={m.is_owner ? "default" : "secondary"}>
                    {m.is_owner ? "Owner" : "Member"}
                  </Badge>
                  {canRemove && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      disabled={removeM.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            `Remove ${name} from the team? They will keep their own account but lose access to this team's credits and deals.`,
                          )
                        ) {
                          removeM.mutate(m.user_id);
                        }
                      }}
                    >
                      Remove
                    </Button>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>

      {me && !isOwner && (
        <div className="mt-6 rounded-md border bg-card p-4">
          <h2 className="text-sm font-medium">Leave team</h2>
          <p className="mt-2 text-xs text-muted-foreground">
            If you leave, your deals stay with the team and no credits are
            refunded. You get a fresh empty solo account.
          </p>
          {leaveError && (
            <p className="mt-2 text-xs text-destructive" role="alert">
              {leaveError}
            </p>
          )}
          <div className="mt-3">
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={leaveM.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    "Leave this team? Your deals stay with the team and you will get a fresh empty solo account.",
                  )
                ) {
                  leaveM.mutate();
                }
              }}
            >
              {leaveM.isPending ? "Leaving…" : "Leave team"}
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}